import { ColoursObj, TDialogStore, useDialogStore } from "@eventr/common-storybook";
import { USER_ADDITIONAL_INFO_SEVERITY } from "@eventr/common/lib/interfaces/orgConfig";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { isViewOpenAt, subscribeNativeViews } from "feat/nativeNavigation/init";
import i18n from "i18n/i18next";
import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { routes } from "routing";
import { useBoundStore } from "state/bound.store";
import { useShallow } from "zustand/react/shallow";

/**
 * The only two screens this dialog may interrupt. Home is where it lands after login; the
 * profile edit sheet re-raises it for a high-severity account that tries to leave without
 * sharing what the organisation requires.
 *
 * Under native navigation this component is portaled once per native view, so every open
 * screen runs this check against its own location. Without the list, a dialog raised at
 * login appears on whichever screen happens to be up — during sign-up that is the terms
 * sheet, before the account exists in any meaningful sense.
 */
const DIALOG_PATHS: string[] = [routes.home.path, routes.account.profileEdit.path];

/**
 * Dialog Handler Component for user required additional info
 * Place this in your root app component or layout
 */
export const UsersRequiredAddiInfoDialogHandler = () => {
  const { replaceDialog, clearDialog } = useDialogStore(
    useShallow((s: TDialogStore) => ({ replaceDialog: s.replaceDialog, clearDialog: s.clearDialog })),
  );

  const { pathname } = useLocation();
  const { pushPath } = useGlobalRouter();

  const {
    showRequiredUserAddInfoDialog,
    showRequiredAddInfoDialog,
    getOrgUsersSeverityLevel,
    profileRequiredAdditionalInfo,
    loginCompleted,
    termsAccepted,
  } = useBoundStore(
    useShallow((s) => ({
      showRequiredUserAddInfoDialog: s.showRequiredUserAddInfoDialog,
      showRequiredAddInfoDialog: s.showRequiredAddInfoDialog,
      getOrgUsersSeverityLevel: s.getOrgUsersSeverityLevel,
      profileRequiredAdditionalInfo: s.profileRequiredAdditionalInfo,
      loginCompleted: s.loginCompleted,
      // The terms gate raises its own screen the moment a profile arrives unaccepted, and
      // owns the session until it is answered. 0 is the only unaccepted value; a profile
      // that has not loaded yet is not held against the user.
      termsAccepted: s.profile?.termsConditionsAcceptedUnix !== 0,
    })),
  );

  // The dialog store is shared, so the topmost view presents the dialog whichever view raised
  // it. Raised the moment terms are accepted, that is the terms sheet, and its own dismissal
  // then closes the native alert as cancelled, which for a high-severity org opens profile
  // edit from the closing sheet. Waiting for the terms view to go keeps the dialog on home.
  const termsSheetOpen = useSyncExternalStore(subscribeNativeViews, () =>
    isViewOpenAt(routes.termsOfService.path),
  );

  // Sign-up runs through login before it reaches the terms screen, so `onLogin` can raise
  // the flag while the session is still being set up. The flag is left standing rather
  // than consumed when this is false: the dialog is deferred to the home screen, not lost.
  const canPresent = loginCompleted && termsAccepted && !termsSheetOpen && DIALOG_PATHS.includes(pathname);

  useEffect(() => {
    if (showRequiredAddInfoDialog && canPresent) {
      const requiredInfoText = profileRequiredAdditionalInfo
        ?.map((info) => i18n.t(`account.profile.additional-info.${info}.name`))
        .join(", ");
      // Medium severity offers Cancel to defer; high shows only Okay, which proceeds
      // straight to the profile edit page.
      const mediumSeverity = getOrgUsersSeverityLevel() === USER_ADDITIONAL_INFO_SEVERITY.medium;
      const navToProfielEditPage = () => {
        clearDialog();
        // Already on the edit-profile sheet: pushing its own route again would
        // dismiss it via the native adapter with nothing to re-present.
        if (pathname !== routes.account.profileEdit.path) {
          pushPath(routes.account.profileEdit.path);
        }
      };

      replaceDialog({
        kind: "confirm",
        text: i18n.t("info-dialog.users-add-info-required-text"),
        footerText: requiredInfoText,
        header: i18n.t("info-dialog.users-add-info-required-header"),
        onConfirm: navToProfielEditPage,
        confirmBtnTextColour: ColoursObj.core.secondary,
        confirmText: mediumSeverity ? i18n.t("info-dialog.okay") : "",
        cancelText: mediumSeverity ? i18n.t("general.cancel") : i18n.t("info-dialog.okay"),
        onCancel: mediumSeverity ? () => clearDialog() : navToProfielEditPage,
        backdropClosable: false,
      });
      showRequiredUserAddInfoDialog(false);
    }
  }, [
    showRequiredAddInfoDialog,
    canPresent,
    replaceDialog,
    showRequiredUserAddInfoDialog,
    clearDialog,
    getOrgUsersSeverityLevel,
    profileRequiredAdditionalInfo,
    pathname,
    pushPath,
  ]);

  return null;
};
